import { env, functionsBase } from '../lib/env'

/**
 * ============================================================================
 *  session-gate is UX, NOT the security boundary.
 * ============================================================================
 *
 * Read that before changing anything here or downstream of it.
 *
 * RLS is the boundary, and it fails closed. An authenticated user with no
 * active `rep_mapping` row matches no rows in orders, order_lines, invoices or
 * stock, and reads only their own (nonexistent) row in rep_mapping. They would
 * see an empty dashboard whether or not this call happens.
 *
 * This gate exists for two much smaller reasons:
 *   1. So an unmapped person sees an honest "not authorized, contact ESL"
 *      instead of a dashboard that looks broken.
 *   2. So the inert auth.users row created by OAuth does not linger as a live
 *      session.
 *
 * If this call were deleted tomorrow, no rep would gain access to another rep's
 * orders. If RLS were disabled tomorrow, every rep would. So: do NOT remove or
 * weaken RLS coverage on the belief that this gate protects the data, and do not
 * treat a 200 from here as authorization to skip a policy.
 * ============================================================================
 */

export type SessionGateVerdict =
  /** Authorized. rep_ids and account_type come from the active mapping rows. */
  | { kind: 'authorized'; repIds: string[]; accountType: string | string[] }
  /** 403 — no active mapping. Route to /not-authorized. */
  | { kind: 'not_authorized'; message: string }
  /** 401 — the token was missing or rejected. Treat as signed out. */
  | { kind: 'no_session' }
  /** Anything else: 503 lookup_failed, 5xx, CORS refusal, network failure. */
  | { kind: 'error'; detail: string }

interface GateBody {
  code?: unknown
  message?: unknown
  rep_ids?: unknown
  account_type?: unknown
}

/**
 * Call session-gate with the caller's access token.
 *
 * MUST be awaited, and its verdict acted on, BEFORE any authenticated surface
 * renders. Never render a dashboard shell and then discover the verdict — that
 * is precisely the flash of wrong state this gate exists to prevent.
 */
export async function callSessionGate(accessToken: string): Promise<SessionGateVerdict> {
  let res: Response
  try {
    res = await fetch(`${functionsBase}/session-gate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.supabasePublishableKey,
        // The USER's token, not the publishable key. session-gate runs with
        // verify_jwt = true and resolves the caller from this header.
        Authorization: `Bearer ${accessToken}`,
      },
    })
  } catch (e) {
    // Also where a CORS refusal lands: if this origin is not in
    // session-gate's ALLOWED_ORIGINS the browser reports a generic network
    // failure, which reads as "auth is broken" rather than "origin missing".
    return {
      kind: 'error',
      detail: `Could not reach session-gate (${e instanceof Error ? e.message : String(e)}). If this is a new origin, check that it is in session-gate's ALLOWED_ORIGINS.`,
    }
  }

  let body: GateBody = {}
  try {
    body = (await res.json()) as GateBody
  } catch {
    // Fall through on the status alone.
  }

  if (res.status === 200 && body.code === 'ok') {
    // rep_id is text (P21 salesrep_id is a string; migration 0006). A prior
    // typeof-number guard here would silently discard every rep_id and read
    // as "authorized with zero reps" instead of failing loudly.
    const repIds = Array.isArray(body.rep_ids)
      ? body.rep_ids.filter((n): n is string => typeof n === 'string')
      : []
    const accountType =
      typeof body.account_type === 'string' || Array.isArray(body.account_type)
        ? (body.account_type as string | string[])
        : []
    return { kind: 'authorized', repIds, accountType }
  }

  if (res.status === 403) {
    return {
      kind: 'not_authorized',
      message:
        typeof body.message === 'string'
          ? body.message
          : 'This account is not set up for the ESL Vision rep portal. Please contact ESL.',
    }
  }

  if (res.status === 401) return { kind: 'no_session' }

  return { kind: 'error', detail: `session-gate returned ${res.status}` }
}
